import React, { useState, useRef, useEffect } from 'react';
import { GripVertical, Maximize2, Download, Settings, X, Pencil, Check } from 'lucide-react';
import InfoButton from '../InfoButton';
import { ChartHeaderProps } from './types';
import { BaselineDropdown } from '../common/BaselineDropdown';

export const ChartHeader: React.FC<ChartHeaderProps> = ({
  title,
  onTitleChange,
  onToggleFullscreen,
  onDownload,
  isFullscreen,
  chartMode,
  onToggleMode,
  availableBaselines,
  selectedBaseline,
  onBaselineChange,
  tooltipOrderMode,
  onTooltipOrderModeChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const settingsDropdownRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const focusWithoutScroll = (el: HTMLElement | null | undefined) => {
    if (!el) return;
    try {
      (el as any).focus?.({ preventScroll: true });
    } catch {
      el.focus();
    }
  };

  const startEditingTitle = () => {
    // Extract only the base card name (before zone info)
    const baseTitle = title.split(' - ')[0];
    setEditedTitle(baseTitle);
    setIsEditingTitle(true);
  };

  const saveTitle = () => {
    if (editedTitle.trim() && onTitleChange) {
      onTitleChange(editedTitle.trim());
    }
    setIsEditingTitle(false);
    setEditedTitle('');
  };

  const cancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
  };

  // Handle click outside to close settings dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && target.closest('.react-resizable-handle')) {
        return; // Ignore clicks originating from resize handle to avoid scroll jumps
      }
      if (
        settingsDropdownRef.current &&
        !settingsDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSettingsDropdownOpen(false);
        focusWithoutScroll(settingsButtonRef.current || undefined);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTooltipOrderModeSelect = (mode: 'value' | 'fixed') => {
    onTooltipOrderModeChange(mode);
    setIsSettingsDropdownOpen(false);
    focusWithoutScroll(settingsButtonRef.current || undefined);
  };

  const formatDateForDisplay = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="flex-shrink-0">
      <div className="p-3 relative overflow-visible">
        {/* Responsive layout: stack on small screens, side-by-side on larger screens */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-3">
          {/* Title and Info section */}
          <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      saveTitle();
                    } else if (e.key === 'Escape') {
                      cancelEditTitle();
                    }
                  }}
                  className="px-2 py-1 text-sm font-medium text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 border border-pink-500 rounded focus:outline-none focus:ring-2 focus:ring-pink-500"
                  style={{ minWidth: '150px' }}
                  autoFocus
                />
                <button
                  onClick={saveTitle}
                  className="p-1 text-green-500 hover:text-green-600 transition-colors"
                  title="Save"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  onClick={cancelEditTitle}
                  className="p-1 text-gray-400 hover:text-gray-500 transition-colors"
                  title="Cancel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {title}
                </h2>
                {onTitleChange && (
                  <button
                    onClick={startEditingTitle}
                    className="p-1 text-gray-400 hover:text-pink-500 transition-colors flex-shrink-0"
                    title="Edit name"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
            <InfoButton title="Chart card">
              <div>
                <div>
                  <span className="font-medium text-gray-800 dark:text-gray-200">Goal:</span>{' '}
                  Visualize time series for selected models/runs per zone.
                </div>
                <div>
                  <span className="font-medium text-gray-800 dark:text-gray-200">Modes:</span>
                  <ul className="list-disc pl-4 mt-1 space-y-1">
                    <li>
                      <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        Data
                      </span>
                      : raw values.
                    </li>
                    <li>
                      <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        Δ Delta
                      </span>
                      : difference vs selected{' '}
                      <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        Baseline
                      </span>
                      .
                    </li>
                  </ul>
                </div>
                <div className="mt-1">
                  <span className="font-medium text-gray-800 dark:text-gray-200">Actions:</span>
                  <span className="ml-1">
                    Use{' '}
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700">Full</span>{' '}
                    for fullscreen,{' '}
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700">PNG</span> to
                    download, and{' '}
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700">
                      Settings
                    </span>{' '}
                    to change tooltip order.
                  </span>
                </div>
              </div>
            </InfoButton>
          </div>

          {/* Controls section - responsive layout */}
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {/* Baseline Selector - only show in delta mode */}
            {chartMode === 'delta' && availableBaselines.length > 0 && (
              <BaselineDropdown
                availableBaselines={availableBaselines}
                selectedBaseline={selectedBaseline}
                onBaselineChange={onBaselineChange}
                displayStyle="dropdown"
              />
            )}

            {/* Mode Toggle */}
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-md">
              <button
                onClick={() => onToggleMode('data')}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  chartMode === 'data'
                    ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                Data
              </button>
              <button
                onClick={() => onToggleMode('delta')}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  chartMode === 'delta'
                    ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                Δ Delta
              </button>
            </div>

            {/* Settings Dropdown */}
            <div className="relative" ref={settingsDropdownRef}>
              <button
                ref={settingsButtonRef}
                onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)}
                className="flex items-center gap-1 px-2 py-[5px] text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors text-gray-600 dark:text-gray-300 focus:outline-none"
                title="Chart Settings"
              >
                <Settings className="h-3 w-3" />
              </button>

              {isSettingsDropdownOpen && (
                <div className="absolute top-full mt-1 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 min-w-56">
                  {/* Tooltip Order Section */}
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                    <div className="text-xs font-medium text-gray-900 dark:text-gray-100">
                      Tooltip Order
                    </div>
                  </div>
                  <div className="py-1 border-b border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleTooltipOrderModeSelect('value')}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        tooltipOrderMode === 'value'
                          ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Order by Value
                    </button>
                    <button
                      onClick={() => handleTooltipOrderModeSelect('fixed')}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        tooltipOrderMode === 'fixed'
                          ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Fixed Order
                    </button>
                  </div>

                  {/* Date Filtering Section */}
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                    <div className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Date Range
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={startDate || ''}
                          onChange={(e) => onStartDateChange(e.target.value || null)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={endDate || ''}
                          onChange={(e) => onEndDateChange(e.target.value || null)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Clear Filters */}
                  {(startDate || endDate) && (
                    <div className="py-1">
                      <button
                        onClick={() => {
                          onStartDateChange(null);
                          onEndDateChange(null);
                        }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-red-600 dark:text-red-400"
                      >
                        Clear Date Filters
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <button
              onClick={onToggleFullscreen}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors text-gray-600 dark:text-gray-300"
              title="Toggle Fullscreen"
            >
              <Maximize2 className="h-3 w-3" />
              {isFullscreen ? 'Exit' : 'Full'}
            </button>
            <button
              onClick={onDownload}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors text-gray-600 dark:text-gray-300"
              title="Download Chart"
            >
              <Download className="h-3 w-3" />
              PNG
            </button>

            {/* Drag Handle */}
            <div className="drag-handle p-1 rounded opacity-40 hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
              <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300" />
            </div>
          </div>
        </div>
      </div>

      {/* Date Filter Pills */}
      {(startDate || endDate) && (
        <div className="px-3 pb-2">
          <div className="flex flex-wrap gap-2">
            {startDate && (
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                <span>From: {formatDateForDisplay(startDate)}</span>
                <button
                  onClick={() => onStartDateChange(null)}
                  className="ml-1 p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded-full transition-colors"
                  title="Remove start date filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {endDate && (
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                <span>To: {formatDateForDisplay(endDate)}</span>
                <button
                  onClick={() => onEndDateChange(null)}
                  className="ml-1 p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded-full transition-colors"
                  title="Remove end date filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
